import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { TENSION_LEVELS, fmtDateFr, plusOneMonth } from './tensionMeta';
import { TensionPill } from './TensionPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TensionDialog = ({ corner, tension, motifs, token, canEdit, onClose, onSaved }) => {
  const [f, setF] = useState({ level: 'tension', motif: '', comment: '', relance_date: plusOneMonth() });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!corner) return;
    setF(tension?.active
      ? { level: tension.level, motif: tension.motif || '', comment: tension.comment || '', relance_date: tension.relance_date || plusOneMonth() }
      : { level: 'tension', motif: '', comment: '', relance_date: plusOneMonth() });
  }, [corner, tension]);

  const save = async (level) => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ca-corner/tensions/${corner.id}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(level === 'none' ? { level } : { ...f, level, relance_date: f.relance_date || null }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur');
      toast.success(level === 'none' ? 'Tension levée' : 'Corner mis en tension');
      onSaved(await r.json());
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!corner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tension-dialog">
        <DialogHeader><DialogTitle>Tension · {corner?.name_powerbi}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <fieldset disabled={!canEdit} className="space-y-4">
          <div className="flex gap-2">
            {Object.entries(TENSION_LEVELS).map(([k, m]) => (
              <button key={k} type="button" onClick={() => setF(p => ({ ...p, level: k }))} data-testid={`tension-level-${k}`}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${f.level === k ? m.pill + ' ring-2 ring-offset-1 ring-current' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                {m.label}
              </button>
            ))}
          </div>
          <label className="block text-sm">Motif
            <select value={f.motif} onChange={e => setF(p => ({ ...p, motif: e.target.value }))} data-testid="tension-motif"
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white dark:bg-slate-900">
              <option value="">— Choisir —</option>
              {motifs.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="block text-sm">Commentaire
            <textarea value={f.comment} onChange={e => setF(p => ({ ...p, comment: e.target.value }))} rows={3} data-testid="tension-comment"
              className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm bg-white dark:bg-slate-900" />
          </label>
          <label className="block text-sm">Date de relance
            <input type="date" value={f.relance_date || ''} onChange={e => setF(p => ({ ...p, relance_date: e.target.value }))} data-testid="tension-relance"
              className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white dark:bg-slate-900" />
          </label>
          </fieldset>
          {tension?.history?.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-500 mb-2">Historique</p>
              <ul className="space-y-2 max-h-40 overflow-y-auto text-xs" data-testid="tension-history">
                {tension.history.map(h => (
                  <li key={h.id} className="flex flex-wrap items-center gap-1.5">
                    <span className="text-slate-400">{fmtDateFr(h.date)}</span>
                    {h.action === 'lift' ? <span className="font-semibold text-emerald-700">Tension levée</span> : <TensionPill level={h.level} />}
                    {h.motif && <span className="font-medium">{h.motif}</span>}
                    <span className="text-slate-400">par {h.user}</span>
                    {h.comment && <span className="w-full text-slate-600 pl-1">« {h.comment} »</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canEdit && <div className="flex justify-between gap-2 pt-1">
            {tension?.active ? (
              <Button variant="outline" disabled={busy} onClick={() => save('none')} data-testid="tension-lift">Lever la tension</Button>
            ) : <span />}
            <Button disabled={busy} onClick={() => save(f.level)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="tension-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Enregistrer
            </Button>
          </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
